import {Button, Text, Tile} from "@wakelet/ui-kit";
import {translateKey} from "../language/translate";
import {inputClass} from "../form/input-class";
import {FC} from "react";
import {useForm} from "react-hook-form";
import {PasswordInput} from "../form/PasswordInput";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {FieldGroup, ValidationError} from "../form/ValidationError";
import {useChangeEmail} from "./useChangeEmail";
import {Spinner} from "../loading/Spinner";
import {getWakeletUrl} from "../../env/env";
import {FieldLabel, RequiredFieldsMessage} from "../form/FieldLabel";

type ChangeEmailFormValues = {
  newEmail: string;
  password: string;
  serverError: void;
};

const schema = yup.object({
  newEmail: yup.string().email(translateKey("account:validation.email")).required(translateKey("account:validation.required.newEmail")),
  password: yup.string().required(translateKey("account:validation.required.password")),
}).required();

export const ChangeEmail: FC = () => {
  const {changeEmail, isSubmitting, isSuccess} = useChangeEmail(2000);
  const {handleSubmit, register, formState: {errors}, setError, setValue} = useForm<ChangeEmailFormValues>({
    resolver: yupResolver(schema),
  });

  const doSubmit = async (values: ChangeEmailFormValues) => {
    if (!changeEmail) {
      return;
    }
    return changeEmail(values.newEmail, values.password).then(() => {
      setValue("newEmail", "");
      setValue("password", "");
    }).catch(err => {
      const {i18nKey} = err;
      setError("serverError", {message: i18nKey.key});
    });
  };

  if (isSuccess) {
    const url = getWakeletUrl("/accounts/reauthenticate?destination=settings");
    window.location.assign(url);
  }

  return <Tile>
    <Text.Heading level={3}>{translateKey("account:change.email.title")}</Text.Heading>
    <form autoComplete="off" onSubmit={handleSubmit(doSubmit)}
          aria-describedby={errors.serverError?.message && "change-email-error"}
    >
      <ValidationError textCenter={true} message={errors.serverError?.message} id="change-email-error"/>
      <FieldGroup>
        <FieldLabel htmlFor="new-email">{translateKey("account:email.label.new")}</FieldLabel>
        <input id="new-email"
               type="text"
               {...register("newEmail")}
               placeholder={translateKey("account:email.label.new")}
               className={inputClass()}
               disabled={isSubmitting}
               autoComplete="email"
               aria-required={true}
               data-testid="new-email-field"
               aria-describedby={errors.newEmail?.message && "email-error"}/>
        <ValidationError message={errors.newEmail?.message} id="email-error"/>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel htmlFor="password">{translateKey("account:placeholders.password")}</FieldLabel>
        <PasswordInput
          id="password"
          {...register("password")}
          placeholder={translateKey("account:placeholders.password")}
          className={inputClass()}
          autoComplete="new-password"
          disabled={isSubmitting}
          data-testid="password-field"
          aria-describedby={errors.password?.message && "password-error"}
          aria-required={true}
        />
        <ValidationError message={errors.password?.message} id="password-error"/>
      </FieldGroup>
      <RequiredFieldsMessage/>
      <Button type="submit" disabled={isSubmitting} data-testid="save-btn"
              className="w-32 flex justify-center items-center float-right mt-2">
        {!isSubmitting && <Button.Label text={translateKey("common:buttons.save")}/>}
        {isSubmitting && <Spinner/>}
      </Button>
    </form>
  </Tile>
};

